import {
  faBug,
  faCheck,
  faFileInvoice,
  faPause,
  faGavel,
  faWarning,
  faLink,
  faSuitcase,
  faCartFlatbedSuitcase,
  faHand,
  IconDefinition,
  faBriefcaseClock,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';
import { useContext } from 'react';

import Tooltip from '@/ui/components/Tooltip';

import { BillingReportContext } from '../contexts/BillingReportProvider';
import { BillingReportRow } from '../types';
import { isHealthieClaimId } from '../utils/isHealthieClaimId';
import { titleize } from '../utils/titleize';

export function BillingRowStatus({
  data: rowData
}: {
  data?: BillingReportRow;
}): JSX.Element | null {
  const { healthieUrl, reportEndAt } = useContext(BillingReportContext);

  if (!rowData) {
    return null;
  }
  const data: BillingReportRow = rowData;

  if (data.isRefreshing) {
    return (
      <span className="flex items-center h-full">
        <Tooltip tooltipContent="Refreshing...">
          <span className="inline-flex items-center text-blue-600">
            <FontAwesomeIcon icon={faSpinner} className="align-middle fa-spin" />
          </span>
        </Tooltip>
      </span>
    );
  }

  if (data.isSubmitting) {
    return (
      <span className="flex items-center h-full">
        <Tooltip tooltipContent="Submitting...">
          <span className="inline-flex items-center text-blue-600">
            <FontAwesomeIcon icon={faSpinner} className="align-middle fa-spin" />
          </span>
        </Tooltip>
      </span>
    );
  }

  const fieldsWithErrors = Object.keys(data.errors || {});
  const fieldsWithWarnings = Object.keys(data.warnings || {});

  function IconWithTooltip({
    content
  }: {
    content?: string | JSX.Element | null;
    iconColor?: string;
    icon?: IconDefinition;
  }) {
    const tooltip = (
      <>
        <div>
          {content && (
            <>
              <b>{content}</b>
              <br />
            </>
          )}
        </div>
        {hasErrors && (
          <div>
            <b>{`${fieldsWithErrors.length} Errors:`}</b>
            {Object.entries(data.errors || []).map(([field, errors]) => (
              <div key={field}>
                <p>{titleize(field)}</p>
                <ul className="list-disc list-inside">
                  {errors.map((error: string, index: number) => (
                    <li key={`${error}-${index}`}>{error}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
        {hasWarnings && (
          <div>
            <b>{fieldsWithWarnings.length} Warnings:</b>
            {Object.entries(data.warnings || []).map(([field, warnings]) => (
              <div key={field}>
                <p>{titleize(field)}</p>
                <ul className="list-disc list-inside">
                  {warnings.map((warning: string) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </>
    );

    return (
      <span className="flex items-center h-full">
        <Tooltip tooltipContent={tooltip}>
          <span
            data-testid={`status-icon-${data.service_id}`}
            className={`inline-flex items-center ${iconColor}`}>
            <FontAwesomeIcon icon={icon} className="align-middle" />
          </span>
        </Tooltip>
      </span>
    );
  }

  const hasIgnoreErrorRequirements = fieldsWithErrors.includes('ignore_error_requirements');
  const hasErrors = fieldsWithErrors.length > 0;
  const hasWarnings = fieldsWithWarnings.length > 0;
  const hasHealthieCmsId = !!data.cms_1500_healthie_id;
  const isOnHold = !!data.on_hold;
  const isIgnoreError = !!data.ignore_error_at;
  const isCaseRate = !!(data.service_class === 'Case Rate');
  const isCaseRateEncounter = !!data.is_case_rate_encounter;
  const isManualHold = !!data.manually_held_at;
  const isManuallyAssociated = !!data.manually_associated_at;
  const notBilledOnCurrentReport =
    !!data.period_end_at && reportEndAt && reportEndAt < new Date(data.period_end_at);

  let iconColor = 'text-green-600';
  if (hasHealthieCmsId && data?.healthie_errors) {
    iconColor = 'text-red-600';
  } else if (hasHealthieCmsId) {
    iconColor = 'text-green-600';
  } else if (notBilledOnCurrentReport) {
    iconColor = 'text-gray-600';
  } else if (isManualHold) {
    iconColor = 'text-yellow-600';
  } else if (hasErrors && isIgnoreError && !hasIgnoreErrorRequirements) {
    iconColor = 'text-blue-600';
  } else if (hasErrors) {
    iconColor = 'text-red-600';
  } else if (hasWarnings) {
    iconColor = 'text-amber-600';
  } else if (isOnHold) {
    iconColor = 'text-yellow-600';
  }

  let icon = faLink;
  if (isManuallyAssociated) {
    icon = faLink;
  } else if (notBilledOnCurrentReport) {
    icon = faBriefcaseClock;
  } else if (hasHealthieCmsId) {
    icon = faFileInvoice;
  } else if (isManualHold) {
    icon = faHand;
  } else if (isCaseRateEncounter) {
    icon = faSuitcase;
  } else if (isCaseRate) {
    icon = faCartFlatbedSuitcase;
  } else if (isIgnoreError) {
    icon = faGavel;
  } else if (hasErrors) {
    icon = faBug;
  } else if (hasWarnings) {
    icon = faWarning;
  } else if (isOnHold) {
    icon = faPause;
  } else {
    icon = faCheck;
  }

  if (hasHealthieCmsId) {
    // This is now a comma separated list of CMS IDs
    const cmsIds = data.cms_1500_healthie_id.split(',');
    const statusIcon = isManuallyAssociated ? (
      <Tooltip
        tooltipContent={`Manually associated with ${data.cms_1500_healthie_id} by ${
          data.manually_associated_by
        } at ${new Date(data.manually_associated_at).toLocaleString()}`}>
        <span className="inline-flex items-center">
          <FontAwesomeIcon icon={icon} className="align-middle" />
        </span>
      </Tooltip>
    ) : data?.healthie_errors ? (
      <IconWithTooltip
        icon={icon}
        iconColor={iconColor}
        content={`This item has errors returned by Healthie during submission: ${data.healthie_errors}`}
      />
    ) : (
      <span className="inline-flex items-center">
        <FontAwesomeIcon icon={icon} className="align-middle" />
      </span>
    );
    return (
      <span className="flex items-center h-full">
        {cmsIds.map((cmsId) =>
          isHealthieClaimId(cmsId) ? (
            <a
              href={`${healthieUrl}/cms1500s/${cmsId}/edit`}
              key={cmsId}
              target="_blank"
              className="text-green-600"
              rel="noreferrer">
              {statusIcon}
            </a>
          ) : (
            <span key={cmsId} className="text-green-600">
              {statusIcon}
            </span>
          )
        )}
      </span>
    );
  } else if (notBilledOnCurrentReport) {
    return (
      <IconWithTooltip
        icon={icon}
        iconColor={iconColor}
        content={`This item will not billed on this report as its period end date is ${new Date(
          data.period_end_at
        ).toLocaleDateString()}`}
      />
    );
  } else if (isManualHold) {
    return (
      <IconWithTooltip
        icon={icon}
        iconColor={iconColor}
        content={`This item has been manually placed on hold`}
      />
    );
  } else if (isCaseRateEncounter) {
    return (
      <IconWithTooltip
        icon={icon}
        iconColor={iconColor}
        content={
          isIgnoreError
            ? 'Encounter will ignore errors'
            : isOnHold
              ? 'Encounter is on hold because of other items on the Claim Group being in error state'
              : 'This is a Case Rate Encounter'
        }
      />
    );
  } else if (isCaseRate) {
    return <IconWithTooltip icon={icon} iconColor={iconColor} content="This is a Case Rate." />;
  } else if (isIgnoreError) {
    return (
      <IconWithTooltip
        icon={icon}
        iconColor={iconColor}
        content={`This item has some errors ignored, see dugout for details`}
      />
    );
  } else if (hasWarnings || hasErrors) {
    return <IconWithTooltip icon={icon} iconColor={iconColor} />;
  } else if (isOnHold) {
    return (
      <IconWithTooltip
        icon={icon}
        iconColor={iconColor}
        content="Other services that would be included for this claim have errors. Resolve or Remove other service lines from the report to bill this item."
      />
    );
  } else {
    return <IconWithTooltip icon={icon} iconColor={iconColor} />;
  }
}
